import { h } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

export interface ShopDialogProps {
  id: string;
  /** id of the element that labels the dialog (its heading), used for aria-labelledby */
  labelledBy: string;
  /** Called after the fade-out finished: unmount the dialog */
  onClose: () => void;
  /** Extra classes on the <dialog>, e.g. a hook for content-specific styles */
  className?: string;
  /**
   * Content of .modal-body. Pass a function to receive close(): it runs the fade-out and then onClose,
   * for content that closes the dialog itself (after a successful submit, for example).
   */
  children: preact.ComponentChildren | ((close: () => void) => preact.ComponentChildren);
}

// Matches Bootstrap's .modal.fade transition (transform .3s, opacity .15s)
const FADE_DURATION_MS = 300;

/**
 * Shell for the shop's Preact modals (add-to-cart, back-in-stock, ...), rendered into document.body while open.
 * Native <dialog> (focus trap, Escape, backdrop for free) dressed in Bootstrap's .modal.fade classes,
 * so Preact stays the only one writing to this DOM. `.e-shop` scopes the shop styles.
 * Open/close animate like a Bootstrap modal: `show` is toggled one frame after showModal()
 * and removed FADE_DURATION_MS before onClose() unmounts us (the backdrop follows via CSS, see _shop-dialog.scss).
 * Below md the dialog is a bottom sheet (same geometry and motion as the cart offcanvas).
 */
export const ShopDialog = ({ id, labelledBy, onClose, className, children }: ShopDialogProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closingRef = useRef(false);
  const [show, setShow] = useState(false);

  // Parents typically pass an inline arrow as onClose, a new function on each of their renders (cart refresh,
  // /pdp-data overlay, ...). Read it through a ref so requestClose stays stable and the open effect below runs
  // exactly once: re-running it would call dialog.focus() again and pull focus out of a field mid-typing.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const requestClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    setShow(false);
    window.setTimeout(() => {
      // Close natively before unmounting: leaves the top layer via the browser's own closing steps and returns
      // focus to the control that opened us. Unmounting an open <dialog> would drop focus to <body>.
      const dialog = dialogRef.current;
      if (dialog?.open) dialog.close();
      onCloseRef.current();
    }, FADE_DURATION_MS);
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    // Focus the container, like Bootstrap's modal does; Tab then moves to the close button as the first
    // control. `autofocus` on the dialog makes showModal() pick the dialog itself instead of the close
    // button, and preventScroll keeps either focus from scrolling the dialog towards the still
    // off-screen sheet on mobile (see the overflow note in _shop-dialog.scss).
    dialog.focus({ preventScroll: true });
    // next frame: add .show so the Bootstrap fade/slide transition runs from the initial state
    const frame = requestAnimationFrame(() => setShow(true));
    // Escape: keep the dialog open, run our fade-out first
    const handleCancel = (event: Event) => {
      event.preventDefault();
      requestClose();
    };
    dialog.addEventListener('cancel', handleCancel);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      cancelAnimationFrame(frame);
      dialog.removeEventListener('cancel', handleCancel);
      document.body.style.overflow = previousOverflow;
    };
  }, [requestClose]); // stable, so this runs once per mount

  return (
    <dialog
      ref={dialogRef}
      id={id}
      className={`shop-dialog modal fade d-block${show ? ' show' : ''}${className ? ` ${className}` : ''}`}
      tabIndex={-1}
      autoFocus
      aria-labelledby={labelledBy}
      onClick={(event) => {
        // .modal-dialog has pointer-events: none, so a click that reaches the dialog itself is a backdrop click
        if (event.target === dialogRef.current) requestClose();
      }}
    >
      <div className="e-shop modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-body p-4">
            <button
              type="button"
              className="btn-close position-absolute top-0 end-0 m-4"
              aria-label="Schließen"
              onClick={requestClose}
            />
            {typeof children === 'function' ? children(requestClose) : children}
          </div>
        </div>
      </div>
    </dialog>
  );
};
